export default {
  slug: "/dental-implants",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/implants.webp",

  en: {
    title: "Dental Implants in Istanbul",
    eyebrow: "Dental care",
    lead:
      "An implant is a titanium screw placed into the jawbone; over three to six months the bone grows onto " +
      "it, and a crown, bridge or full arch is then fixed on top. It replaces the root, which is why it does " +
      "not depend on the teeth either side.",
    cta: "Book a free assessment",
    heroAlt: "Dental implant consultation at Platinya Clinic",
    stats: [
      { value: "3–6 months", label: "Bone healing before the crown" },
      { value: "45–90 min", label: "Single implant placement" },
      { value: "10–15 years+", label: "Function with care" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "The deciding factors are how much bone there is, how good its quality is, the state of the gums, " +
        "and how well any general condition is controlled.",
      items: [
        "One missing tooth, with healthy neighbours that should not be cut down for a bridge",
        "Several missing teeth in the same arch, or a full arch that is loose or no longer wearable",
        "Enough bone — or bone that can be rebuilt with a graft or a sinus lift",
        "Controlled diabetes, no smoking or a willingness to stop, and no recent radiotherapy or antiresorptive treatment to the jaw",
      ],
      note:
        "Smoking is the single most useful thing to change: it roughly doubles the risk of an implant failing " +
        "to integrate, and the effect follows how much is smoked. Uncontrolled diabetes affects healing in a " +
        "similar way.",
    },
    techniques: {
      heading: "Single tooth, several teeth, or a full arch",
      intro:
        "The number and position of implants is decided by the bone that exists and by how the bite loads it " +
        "— not by how many teeth are missing.",
      items: [
        {
          name: "Single implant and crown",
          text:
            "The most conservative replacement for one missing tooth: nothing is cut off the neighbouring " +
            "teeth. Placement takes 45–90 minutes under local anaesthetic, followed by three to six months of " +
            "healing before the crown is made.",
        },
        {
          name: "Implant-supported bridge",
          text:
            "For several missing teeth in a row, two or more implants carry a bridge, so the teeth at either " +
            "end stay untouched. Fewer implants than teeth replaced — which is why the span has to be " +
            "planned carefully, not extended to save one fixture.",
        },
        {
          name: "All-on-4 and All-on-6",
          text:
            "A full arch fixed on four or six implants, with the rear ones tilted to use the bone that " +
            "remains and often avoid grafting. A provisional bridge can usually be fitted within a few days; " +
            "the final one waits for healing. Not everyone qualifies — bone volume, bite and lip support " +
            "decide.",
        },
      ],
    },
    journey: {
      heading: "From the scan to the final crown",
      steps: [
        {
          name: "Assessment and planning",
          text:
            "A CBCT scan measures bone height and width and shows where the nerve and the sinus sit. If the " +
            "bone is thin, a graft or a sinus lift is planned first, which adds four to six months before " +
            "implant placement.",
        },
        {
          name: "Placement",
          text:
            "Under local anaesthetic, 45–90 minutes for a single implant. Stability is recorded, and any " +
            "temporary tooth or denture is adjusted so it does not load the implant while it heals.",
        },
        {
          name: "Osseointegration",
          text:
            "Three to six months in which the implant is left alone. The lower jaw is usually ready sooner " +
            "than the upper, and grafted sites take longer. Smoking and poorly controlled diabetes slow this " +
            "stage down.",
        },
        {
          name: "Abutment and impression",
          text:
            "The gum is shaped around a healing abutment, then a scan or impression is taken and the crown " +
            "or bridge is made — usually one to three weeks in the laboratory.",
        },
        {
          name: "Fitting and maintenance",
          text:
            "The restoration is screwed or cemented in place and the bite is checked. Hygiene visits every " +
            "six months, with an x-ray at one year and then periodically: bone loss around an implant is " +
            "silent until it is advanced.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "Implant surgery is routine but it is still surgery into bone, and the failure modes are specific. " +
        "These are the ones that matter.",
      items: [
        "Failure to integrate: the implant loosens and has to be removed — more likely in smokers, thin bone and uncontrolled diabetes",
        "Peri-implantitis, bone loss around a working implant, usually driven by plaque; it does not reverse on its own",
        "Nerve injury in the lower jaw causing numbness of the lip, chin or tongue — usually temporary, occasionally permanent",
        "Sinus perforation or graft failure in the upper jaw, which may need a second procedure",
        "Component fracture, a loosened screw or a chipped crown — repairable, but not free",
      ],
      note:
        "An implant is not a tooth. It has no ligament, reduced feedback when you bite too hard, and once " +
        "bone is lost around it, no way to grow it back. Hygiene and a night guard matter more than the brand " +
        "on the box.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Is the surgery painful?",
          a:
            "With local anaesthetic you feel pressure and vibration rather than pain. The first two days are " +
            "bruised and swollen, and ordinary paracetamol or ibuprofen is usually enough; most people are " +
            "back to desk work the next day.",
        },
        {
          q: "How long does the whole thing take?",
          a:
            "Four to eight months from placement to final crown for a single implant, because the bone sets " +
            "the timetable. A full arch on All-on-4 can have a fixed provisional within days, with the final " +
            "bridge at four to six months.",
        },
        {
          q: "Can I smoke?",
          a:
            "Ideally not at all during treatment. Stopping at least a week before placement and for about " +
            "eight weeks after measurably improves the odds of integration; smoking afterwards raises the " +
            "long-term risk of bone loss around the implant.",
        },
        {
          q: "What if I do not have enough bone?",
          a:
            "Bone can be added — particulated graft with a membrane for width, a block graft for larger " +
            "defects, or a sinus lift for the upper back jaw. It is a separate procedure with its own healing " +
            "time, and it is planned from the scan rather than discovered during surgery.",
        },
      ],
    },
    ctaBand: {
      heading: "Send your panoramic x-ray",
      text:
        "A recent panoramic x-ray or CBCT, plus a note of which teeth are missing, is enough to say whether " +
        "grafting is likely, roughly how many implants, and how long the plan would run.",
      button: "Send your x-ray",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the mouth, " +
      "reading a CBCT scan and reviewing the medical history; results vary between patients.",
  },

  ar: {
    title: "زراعة الأسنان في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "الغرسة برغي من التيتانيوم يُوضع في عظم الفك، وينمو العظم عليه خلال ثلاثة إلى ستة أشهر، ثم يُثبَّت " +
      "عليه تاج أو جسر أو قوس كامل. وهي تعوّض الجذر، ولهذا لا تعتمد على الأسنان المجاورة.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "استشارة زراعة الأسنان في عيادة بلاتينيا",
    stats: [
      { value: "٣–٦ أشهر", label: "التئام العظم قبل التاج" },
      { value: "٤٥–٩٠ دقيقة", label: "زرع غرسة واحدة" },
      { value: "١٠–١٥ سنة فأكثر", label: "الوظيفة مع العناية" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "العوامل الحاسمة هي كمية العظم وجودته وحالة اللثة ومدى ضبط أي مرض عام.",
      items: [
        "سن واحد مفقود، والأسنان المجاورة سليمة ولا يجب بردها لجسر",
        "عدة أسنان مفقودة في القوس نفسه، أو قوس كامل متحرّك أو لم يعد قابلاً للاستخدام",
        "عظم كافٍ — أو عظم يمكن إعادة بنائه بترقيع أو رفع الجيب الفكي",
        "سكري مضبوط، ولا تدخين أو استعداد للتوقّف، ولا علاج إشعاعي حديث ولا أدوية مانعة لارتشاف العظم في الفك",
      ],
      note:
        "التدخين هو أهم عامل يمكن تغييره: يضاعف تقريباً خطر فشل اندماج الغرسة، والأثر يتناسب مع كمية " +
        "التدخين. والسكري غير المضبوط يؤثّر في الالتئام بصورة مشابهة.",
    },
    techniques: {
      heading: "سن واحد أم عدة أسنان أم قوس كامل",
      intro:
        "عدد الغرسات وموضعها يحدّدهما العظم الموجود وطريقة تحميل الإطباق عليه — لا عدد الأسنان المفقودة.",
      items: [
        {
          name: "غرسة واحدة وتاج",
          text:
            "أكثر الحلول تحفّظاً لسن واحد مفقود: لا يُبرد أي سن مجاور. يستغرق الزرع ٤٥ إلى ٩٠ دقيقة تحت " +
            "تخدير موضعي، ثم ثلاثة إلى ستة أشهر من الالتئام قبل صناعة التاج.",
        },
        {
          name: "جسر محمول على غرسات",
          text:
            "عند فقدان عدة أسنان متتالية، تحمل غستران أو أكثر جسراً، فتبقى الأسنان في الطرفين سليمة دون " +
            "برد. عدد الغرسات أقل من عدد الأسنان المعوّضة — ولهذا يجب تخطيط الامتداد بدقة لا تمديده لتوفير " +
            "غرسة.",
        },
        {
          name: "All-on-4 وAll-on-6",
          text:
            "قوس كامل يُثبَّت على أربع أو ست غرسات، وتُمال الغرسات الخلفية للاستفادة من العظم المتبقّي " +
            "وتجنّب الترقيع غالباً. ويمكن تركيب جسر مؤقت في أيام قليلة عادةً، أما الجسر النهائي فينتظر " +
            "الالتئام. ولا يصلح لكل مريض — فحجم العظم والإطباق ودعم الشفة هي ما يقرّر.",
        },
      ],
    },
    journey: {
      heading: "من الأشعة إلى التاج النهائي",
      steps: [
        {
          name: "التقييم والتخطيط",
          text:
            "يقيس تصوير CBCT ارتفاع العظم وعرضه ويُظهر موضع العصب والجيب الفكي. وإذا كان العظم رقيقاً " +
            "يُخطَّط لترقيع أو رفع جيب أولاً، ما يضيف أربعة إلى ستة أشهر قبل الزرع.",
        },
        {
          name: "الزرع",
          text:
            "تحت تخدير موضعي، ٤٥ إلى ٩٠ دقيقة للغرسة الواحدة. ويُسجَّل ثبات الغرسة، ويُعدَّل أي سن مؤقت " +
            "أو طقم متحرّك كي لا يحمّل الغرسة أثناء التئامها.",
        },
        {
          name: "الاندماج العظمي",
          text:
            "ثلاثة إلى ستة أشهر تُترك فيها الغرسة دون تحميل. والفك السفلي جاهز عادةً قبل العلوي، والمواضع " +
            "المُرقَّعة تحتاج وقتاً أطول. والتدخين والسكري غير المضبوط يبطئان هذه المرحلة.",
        },
        {
          name: "الدعامة والطبعة",
          text:
            "تُشكَّل اللثة حول دعامة شفاء، ثم يُؤخذ مسح أو طبعة ويُصنع التاج أو الجسر — أسبوع إلى ثلاثة " +
            "أسابيع في المختبر عادةً.",
        },
        {
          name: "التثبيت والصيانة",
          text:
            "يُثبَّت الترميم بالبراغي أو بالإسمنت ويُفحص الإطباق. ثم زيارات عناية كل ستة أشهر مع صورة " +
            "شعاعية بعد سنة ثم دورياً: ففقد العظم حول الغرسة صامت حتى يتقدّم.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "زراعة الأسنان إجراء معتاد لكنه جراحة في العظم، وأنماط فشله محدّدة. وهذه أهمّها.",
      items: [
        "فشل الاندماج: تتحرّك الغرسة ويلزم نزعها — واحتماله أعلى عند المدخّنين وفي العظم الرقيق والسكري غير المضبوط",
        "التهاب حول الغرسة (Peri-implantitis) وفقد عظم حول غرسة عاملة، سببه اللويحة غالباً؛ ولا يتراجع من تلقاء نفسه",
        "إصابة العصب في الفك السفلي بتنميل في الشفة أو الذقن أو اللسان — مؤقّت عادةً ودائم أحياناً",
        "انثقاب الجيب الفكي أو فشل الترقيع في الفك العلوي، وقد يستدعي إجراءً ثانياً",
        "كسر أحد المكوّنات أو ارتخاء برغي أو كسر التاج — قابل للإصلاح لكنه ليس مجانياً",
      ],
      note:
        "الغرسة ليست سناً. لا رباط لها، والإحساس بالعضّ الزائد لديها أقل، وإذا فُقد العظم حولها لا يعود. " +
        "العناية والواقي الليلي أهم من اسم الشركة المصنّعة.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل الجراحة مؤلمة؟",
          a:
            "مع التخدير الموضعي تشعر بضغط واهتزاز لا بألم. واليومان الأولان فيهما تورّم وكدمات، ويكفي " +
            "الباراسيتامول أو الإيبوبروفين عادةً؛ ومعظم الناس يعودون للعمل المكتبي في اليوم التالي.",
        },
        {
          q: "كم تستغرق العملية كاملة؟",
          a:
            "أربعة إلى ثمانية أشهر من الزرع إلى التاج النهائي للغرسة الواحدة، لأن العظم هو من يضع الجدول. " +
            "أما القوس الكامل على أربع غرسات فيمكن أن يحصل على جسر مؤقت ثابت في أيام، والنهائي في أربعة " +
            "إلى ستة أشهر.",
        },
        {
          q: "هل يمكنني التدخين؟",
          a:
            "الأفضل الامتناع تماماً خلال العلاج. والتوقّف أسبوعاً على الأقل قبل الزرع ونحو ثمانية أسابيع " +
            "بعده يحسّن فرص الاندماج بصورة ملموسة؛ والتدخين بعد ذلك يرفع خطر فقد العظم حول الغرسة على المدى " +
            "الطويل.",
        },
        {
          q: "وإذا لم يكن العظم كافياً؟",
          a:
            "يمكن إضافة عظم — ترقيع حبيبي مع غشاء لزيادة العرض، أو ترقيع كتلي للعيوب الأكبر، أو رفع الجيب " +
            "الفكي للخلف العلوي. وهو إجراء منفصل بزمن التئام خاص، ويُخطَّط له من الأشعة لا يُكتشف أثناء " +
            "الجراحة.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورتك البانورامية",
      text:
        "صورة بانورامية حديثة أو تصوير CBCT، مع ذكر الأسنان المفقودة، تكفي لنقول هل يُرجَّح الترقيع، وكم " +
        "غرسة تقريباً، وكم ستستغرق الخطة.",
      button: "أرسل أشعتك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص الفم وقراءة تصوير CBCT " +
      "ومراجعة التاريخ المرضي؛ والنتائج تختلف من مريض لآخر.",
  },
};
